'use client'

import { useCallback, useMemo } from 'react'
import { useSession } from 'next-auth/react'

import { reportSentryError } from 'lib/sentry'
import { useCartStore } from 'modules/cart'
import { extractTokenFromClientSession } from 'modules/customer'
import toast from 'shared/ui/utils/toast'
import { getApiErrors } from 'shared/utils/errors'

import { SingleSaleOrderResponse } from '../../../types'
import { BlikErrorType, PaymentMethodsNames } from '../../payment/types'
import * as editSaleOrderApi from '../api'
import { EditSaleOrderCart } from '../types'
import { restorePreEditCart } from '../utils'

interface FinishEditSaleOrderParams {
  saleOrderId: string
  cartId: string
  paymentMethodId: string
  blikCode?: string
  cancelLoading?: () => void
}

export const useEditSaleOrderActions = () => {
  const session = useSession()
  const token = extractTokenFromClientSession(session)
  const setCart = useCartStore(state => state.setCart)
  const createCart = useCartStore(state => state.createCart)
  const setIsLoading = useCartStore(state => state.setIsLoading)

  const restoreCart = useCallback(
    () => restorePreEditCart({ setCart, createCart: () => createCart(session) }),
    [setCart, createCart, session]
  )

  const beginEditSaleOrder = useCallback(
    async (saleOrderId: string): Promise<EditSaleOrderCart | null> => {
      try {
        const result = await editSaleOrderApi.beginEditSaleOrder({
          saleOrderId,
          token
        })

        if (result) {
          const newCart = result?.data?.data || null

          await createCart(session)
          toast.success('Edycja zamówienia została rozpoczęta')

          return newCart
        }

        return null
      } catch (err) {
        toast.error('Nie udało się utworzyć nowego koszyka')
        reportSentryError(err, 'Failed to start edit order')

        return null
      }
    },
    [token, createCart, session]
  )

  const cancelEditSaleOrder = useCallback(
    async (saleOrderId: string): Promise<void> => {
      try {
        const result = await editSaleOrderApi.cancelEditSaleOrder({
          saleOrderId,
          token
        })

        if (result.status === 200) {
          await restoreCart()
          toast.success('Edycja zamówienia została anulowana')
        }
      } catch (err) {
        await restoreCart()
        toast.error('Nie udało się anulować edycji zamówienia')

        reportSentryError(err, 'ailed to cancel edit order:')
      }
    },
    [token, restoreCart]
  )

  const finishEditSaleOrder = useCallback(
    async ({
      saleOrderId,
      cartId,
      paymentMethodId,
      blikCode,
      cancelLoading
    }: FinishEditSaleOrderParams): Promise<SingleSaleOrderResponse | null> => {
      try {
        const result = await editSaleOrderApi.finishEditSaleOrder({
          saleOrderId,
          cartId,
          paymentMethodId,
          blikCode,
          token
        })

        if (result) {
          await restoreCart()
          toast.success('Edytowano zamówienie.')

          return result
        } else {
          await restoreCart()

          return null
        }
      } catch (err) {
        const error = err as BlikErrorType

        if (
          error?.response?.data?.paymentMethodName === PaymentMethodsNames.Blik
        ) {
          // Callers catch the BLIK error payload itself (paymentMethodName, …), not an Error.
          throw error?.response?.data as unknown as Error
        } else {
          await restoreCart()
        }
        setIsLoading(false)
        toast.error(getApiErrors(err))
        cancelLoading && cancelLoading()

        return null
      }
    },
    [token, restoreCart, setIsLoading]
  )

  return useMemo(
    () => ({ beginEditSaleOrder, cancelEditSaleOrder, finishEditSaleOrder }),
    [beginEditSaleOrder, cancelEditSaleOrder, finishEditSaleOrder]
  )
}
